import config from '../config';
import posthog from 'posthog-js';
import axios from 'axios';
import { getStorage } from 'helpers/storage';

// apiGet/apiPost/apiPatch/apiDelete callers only ever pass guid, never jwt - none of them were
// ever wired up to send Authorization: Bearer. Rather than threading jwt through 365 call sites,
// resolve it here from whichever storage bucket that guid actually belongs to (mirrors the
// user/tempUser/spoofedUser + userJwt/tempUserJwt/spoofedUserJwt pairs set at login - see
// actions/user/index.js).
const jwtForGuid = guid => {
    if (!guid) return undefined;
    if (getStorage('spoofedUser') === guid) return getStorage('spoofedUserJwt');
    if (getStorage('user') === guid) return getStorage('userJwt');
    if (getStorage('tempUser') === guid) return getStorage('tempUserJwt');
    return undefined;
};

// Closes the gap where a new/edited axios call site forgets to call apiConfig(...) or
// mis-threads the jwt argument: unlike fetch, axios exposes a true request interceptor that
// runs on every request sent through this shared axios module, regardless of how the caller
// built its config. Additive to (not a replacement for) apiConfig's own jwt resolution -
// whichever attaches Authorization first wins; this never overwrites a header already present.
axios.interceptors.request.use(requestConfig => {
    try {
        const hasAuthHeader = !!requestConfig.headers?.get?.('Authorization');
        if (!hasAuthHeader) {
            const guid = requestConfig.headers?.get ? requestConfig.headers.get('token') : requestConfig.headers?.token;
            const resolvedJwt = jwtForGuid(guid);
            if (resolvedJwt) {
                if (requestConfig.headers?.set) {
                    requestConfig.headers.set('Authorization', `Bearer ${resolvedJwt}`);
                } else {
                    requestConfig.headers = { ...requestConfig.headers, Authorization: `Bearer ${resolvedJwt}` };
                }
            }
        }
    } catch (e) {
        // Never block a request over auth-header resolution failure - fall through unmodified.
    }
    return requestConfig;
});

export const apiCode = withAmpersand => {
    // Live with ampersand
    // Live without ampersand
    // Dev with ampersand
    // Dev without ampersand
    return config.apiGateway.CODE
        ? withAmpersand
            ? `?code=${config.apiGateway.CODE}&`
            : `?code=${config.apiGateway.CODE}`
        : withAmpersand
        ? '?'
        : '';
};

export const apiConfig = (guid, files, immutable, cancelToken, jwt) => {
    const resolvedJwt = jwt || jwtForGuid(guid);
    return {
        cancelToken,
        headers: {
            'Access-Control-Allow-Origin': '*',
            'Cache-Control': immutable ? 'max-age=3600' : 'no-cache',
            token: guid ? guid : '',
            ...(resolvedJwt ? { Authorization: `Bearer ${resolvedJwt}` } : {}),
            'Content-Type': files ? 'multipart/form-data' : 'application/json',
        },
    };
};

export const apiGet = (url, guid, success, failure, returnText, overrideHeaders) => {
    const baseHeaders = apiConfig(guid);
    if (overrideHeaders) baseHeaders.headers = overrideHeaders;
    const headers = { ...baseHeaders, method: 'GET' };
    const codePrefix = url.indexOf('?') > 0 ? '&' : '?';

    const fullUrl =
        url.indexOf('http') >= 0 ? url : `${config.apiGateway.URL}${url}${codePrefix}code=${config.apiGateway.CODE}`;

    return fetch(fullUrl, headers)
        .then(res => (returnText ? res.text() : res.json()))
        .then(response => success?.(response))
        .catch(err => (failure ? failure(err) : console.error(err)));
};

export const apiPost = (url, object, guid, success, failure, method, returnType, extraHeaders) => {
    const headers = {
        ...extraHeaders,
        ...apiConfig(guid),
        method: method || 'POST',
        body: object ? JSON.stringify(object) : null,
    };

    const codePrefix = url.indexOf('?') > 0 ? '&' : '?';

    return fetch(`${config.apiGateway.URL}${url}${codePrefix}code=${config.apiGateway.CODE}`, headers)
        .then(res => {
            return returnType === 'text' ? res.text() : returnType === 'json' ? res.json() : res;
        })
        .then(response => {
            if (response?.ok || typeof response?.ok === 'undefined') {
                success?.(response);
            } else {
                if (response?.status === 409) {
                    posthog.capture('data-conflict', { url: url, data: object });
                }
                failure?.({ response });
            }
        })
        .catch(err => failure?.(err));
};

export const apiPatch = (url, object, guid, success, failure, method, returnType, extraHeaders) => {
    apiPost(url, object, guid, success, failure, method || 'PATCH', returnType, extraHeaders);
};

export const apiDelete = (url, object, guid, success, failure, method, returnType, extraHeaders) => {
    apiPost(url, object, guid, success, failure, method || 'DELETE', returnType, extraHeaders);
};

export const apiDownloadFile = (url, object, guid, success, failure, method, extraHeaders) => {
    const headers = {
        ...extraHeaders,
        ...apiConfig(guid),
        method: method || 'POST',
        body: object ? JSON.stringify(object) : null,
    };

    const codePrefix = url.indexOf('?') > 0 ? '&' : '?';

    fetch(`${config.apiGateway.URL}${url}${codePrefix}code=${config.apiGateway.CODE}`, headers)
        .then(response => {
            if (response.ok) {
                const blob = response.blob();
                const filenameHeader = response.headers.get('Content-Disposition');
                let filename = filenameHeader?.split('filename=')[1];
                if (filename) {
                    filename = filename.trim().replace(/^"|"$/g, '');
                }

                return Promise.all([blob, filename, response]);
            }

            return Promise.all([response]);
        })
        .then(([blob, filename, response]) => {
            if (response.ok) {
                const a = document.createElement('a');
                document.body.appendChild(a);
                const url = window.URL.createObjectURL(blob);
                a.style = 'display: none';
                a.href = url;
                a.download = filename;
                a.click();
                window.URL.revokeObjectURL(url);

                success?.(filename);
            } else {
                failure?.({ response });
            }
        })
        .catch(err => {
            failure?.({ err });
        });
};
